import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { ModelArchitecture, LayerWithVisualization, Layer, ClassificationLabel, Architecture } from '../types/modelBuilder';
import { v4 as uuidv4 } from 'uuid';

interface ModelBuilderContextType {
  architecture: ModelArchitecture | null;
  selectedLayerId: string | null;
  layers: LayerWithVisualization[];
  history: ModelArchitecture[];
  historyIndex: number;

  // Actions
  createNewArchitecture: (name: string, description?: string) => void;
  loadArchitecture: (architecture: ModelArchitecture | Architecture) => void;
  addLayer: (layer: LayerWithVisualization) => void;
  removeLayer: (layerId: string) => void;
  updateLayer: (layerId: string, updates: Partial<LayerWithVisualization>) => void;
  selectLayer: (layerId: string | null) => void;
  connectLayers: (fromId: string, toId: string) => void;
  disconnectLayers: (fromId: string, toId: string) => void;
  updateClassificationLabels: (labels: ClassificationLabel[]) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  getArchitectureJSON: () => string;
  duplicateLayer: (layerId: string) => void;
}

const ModelBuilderContext = createContext<ModelBuilderContextType | undefined>(undefined);

type Action =
  | { type: 'CREATE_NEW_ARCHITECTURE'; name: string; description?: string }
  | { type: 'LOAD_ARCHITECTURE'; architecture: ModelArchitecture | Architecture }
  | { type: 'ADD_LAYER'; layer: LayerWithVisualization }
  | { type: 'REMOVE_LAYER'; layerId: string }
  | { type: 'UPDATE_LAYER'; layerId: string; updates: Partial<LayerWithVisualization> }
  | { type: 'SELECT_LAYER'; layerId: string | null }
  | { type: 'CONNECT_LAYERS'; fromId: string; toId: string }
  | { type: 'DISCONNECT_LAYERS'; fromId: string; toId: string }
  | { type: 'UPDATE_CLASSIFICATION_LABELS'; labels: ClassificationLabel[] }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'RESET' }
  | { type: 'DUPLICATE_LAYER'; layerId: string };

interface ReducerState {
  architecture: ModelArchitecture | null;
  selectedLayerId: string | null;
  layers: LayerWithVisualization[];
  history: ModelArchitecture[];
  historyIndex: number;
}

const initialState: ReducerState = {
  architecture: null,
  selectedLayerId: null,
  layers: [],
  history: [],
  historyIndex: -1,
};

// Convert backend Architecture format to frontend ModelArchitecture format
const convertBackendToFrontend = (backendArch: Architecture): ModelArchitecture => {
  const layers: LayerWithVisualization[] = backendArch.layers.map((layer, index) => ({
    ...layer,
    position: {
      x: index * 200,
      y: 0,
      z: 0,
    },
    name: layer.id,
  }));

  return {
    id: uuidv4(),
    name: 'Imported Architecture',
    version: '1.0',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    layers,
    inputShape: [],
    outputShape: [],
    classificationLabels: [],
    metadata: {},
  };
};

// Convert frontend ModelArchitecture to backend Architecture format
const convertFrontendToBackend = (frontendArch: ModelArchitecture): Architecture => {
  const layers: Layer[] = frontendArch.layers.map(({ position, name, inputShape, outputShape, ...layer }) => layer);
  return { layers };
};

const reducer = (state: ReducerState, action: Action): ReducerState => {
  switch (action.type) {
    case 'CREATE_NEW_ARCHITECTURE': {
      const newArch: ModelArchitecture = {
        id: uuidv4(),
        name: action.name,
        description: action.description,
        version: '1.0',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        layers: [],
        inputShape: [],
        outputShape: [],
        classificationLabels: [],
        metadata: {},
      };
      return {
        ...state,
        architecture: newArch,
        layers: [],
        history: [newArch],
        historyIndex: 0,
      };
    }

    case 'LOAD_ARCHITECTURE': {
      // Check if it's backend format (has layers array directly) or frontend format
      let frontendArch: ModelArchitecture;
      if ('layers' in action.architecture && action.architecture.layers.length > 0) {
        // Check if first layer has 'inputs' property (backend format) or 'connections' (old frontend format)
        const firstLayer = action.architecture.layers[0];
        if ('inputs' in firstLayer && !('connections' in firstLayer)) {
          // Backend format - convert it
          frontendArch = convertBackendToFrontend(action.architecture as Architecture);
        } else {
          // Frontend format (old or new)
          frontendArch = action.architecture as ModelArchitecture;
          // Convert old format to new format if needed
          if (frontendArch.layers.some(l => 'connections' in l)) {
            frontendArch = {
              ...frontendArch,
              layers: frontendArch.layers.map(layer => {
                const oldLayer = layer as any;
                if (oldLayer.connections) {
                  const { connections, ...rest } = oldLayer;
                  return {
                    ...rest,
                    inputs: connections?.from || [],
                  } as LayerWithVisualization;
                }
                return layer;
              }),
            };
          }
        }
      } else {
        frontendArch = action.architecture as ModelArchitecture;
      }

      return {
        ...state,
        architecture: frontendArch,
        layers: frontendArch.layers,
        history: [frontendArch],
        historyIndex: 0,
      };
    }

    case 'ADD_LAYER': {
      if (!state.architecture) return state;

      const newLayers = [...state.layers, action.layer];
      const newArch: ModelArchitecture = {
        ...state.architecture,
        layers: newLayers,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        layers: newLayers,
        architecture: newArch,
        history: [...state.history.slice(0, state.historyIndex + 1), newArch],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'REMOVE_LAYER': {
      if (!state.architecture) return state;

      const newLayers = state.layers.filter(l => l.id !== action.layerId);

      // Remove references to this layer from other layers' inputs
      newLayers.forEach(layer => {
        layer.inputs = layer.inputs.filter(id => id !== action.layerId);
      });

      const newArch: ModelArchitecture = {
        ...state.architecture,
        layers: newLayers,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        layers: newLayers,
        architecture: newArch,
        selectedLayerId: state.selectedLayerId === action.layerId ? null : state.selectedLayerId,
        history: [...state.history.slice(0, state.historyIndex + 1), newArch],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'UPDATE_LAYER': {
      if (!state.architecture) return state;

      const newLayers = state.layers.map(layer =>
        layer.id === action.layerId ? { ...layer, ...action.updates } : layer
      );

      const newArch: ModelArchitecture = {
        ...state.architecture,
        layers: newLayers,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        layers: newLayers,
        architecture: newArch,
        history: [...state.history.slice(0, state.historyIndex + 1), newArch],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'SELECT_LAYER': {
      return {
        ...state,
        selectedLayerId: action.layerId,
      };
    }

    case 'CONNECT_LAYERS': {
      if (!state.architecture) return state;

      const newLayers = state.layers.map(layer => {
        if (layer.id === action.toId) {
          // Add fromId to inputs array if not already present
          const uniqueInputs = Array.from(new Set([...layer.inputs, action.fromId]));
          return {
            ...layer,
            inputs: uniqueInputs,
          };
        }
        return layer;
      });

      const newArch: ModelArchitecture = {
        ...state.architecture,
        layers: newLayers,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        layers: newLayers,
        architecture: newArch,
        history: [...state.history.slice(0, state.historyIndex + 1), newArch],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'DISCONNECT_LAYERS': {
      if (!state.architecture) return state;

      const newLayers = state.layers.map(layer => {
        if (layer.id === action.toId) {
          return {
            ...layer,
            inputs: layer.inputs.filter(id => id !== action.fromId),
          };
        }
        return layer;
      });

      const newArch: ModelArchitecture = {
        ...state.architecture,
        layers: newLayers,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        layers: newLayers,
        architecture: newArch,
        history: [...state.history.slice(0, state.historyIndex + 1), newArch],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'UPDATE_CLASSIFICATION_LABELS': {
      if (!state.architecture) return state;

      const newArch: ModelArchitecture = {
        ...state.architecture,
        classificationLabels: action.labels,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        architecture: newArch,
        history: [...state.history.slice(0, state.historyIndex + 1), newArch],
        historyIndex: state.historyIndex + 1,
      };
    }

    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      const arch = state.history[newIndex];
      return {
        ...state,
        architecture: arch,
        layers: arch.layers,
        historyIndex: newIndex,
      };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIndex = state.historyIndex + 1;
      const arch = state.history[newIndex];
      return {
        ...state,
        architecture: arch,
        layers: arch.layers,
        historyIndex: newIndex,
      };
    }

    case 'RESET': {
      return initialState;
    }

    case 'DUPLICATE_LAYER': {
      if (!state.architecture) return state;

      const layerToDuplicate = state.layers.find(l => l.id === action.layerId);
      if (!layerToDuplicate) return state;

      const newLayer: LayerWithVisualization = {
        ...layerToDuplicate,
        id: uuidv4(),
        name: `${layerToDuplicate.name || layerToDuplicate.id} (copy)`,
        position: {
          x: layerToDuplicate.position.x + 100,
          y: layerToDuplicate.position.y + 100,
          z: layerToDuplicate.position.z,
        },
        inputs: [], // Start with no inputs
      };

      const newLayers = [...state.layers, newLayer];
      const newArch: ModelArchitecture = {
        ...state.architecture,
        layers: newLayers,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...state,
        layers: newLayers,
        architecture: newArch,
        history: [...state.history.slice(0, state.historyIndex + 1), newArch],
        historyIndex: state.historyIndex + 1,
      };
    }

    default:
      return state;
  }
};

export const ModelBuilderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const createNewArchitecture = useCallback((name: string, description?: string) => {
    dispatch({ type: 'CREATE_NEW_ARCHITECTURE', name, description });
  }, []);

  const loadArchitecture = useCallback((architecture: ModelArchitecture | Architecture) => {
    dispatch({ type: 'LOAD_ARCHITECTURE', architecture });
  }, []);

  const addLayer = useCallback((layer: LayerWithVisualization) => {
    dispatch({ type: 'ADD_LAYER', layer });
  }, []);

  const removeLayer = useCallback((layerId: string) => {
    dispatch({ type: 'REMOVE_LAYER', layerId });
  }, []);

  const updateLayer = useCallback((layerId: string, updates: Partial<LayerWithVisualization>) => {
    dispatch({ type: 'UPDATE_LAYER', layerId, updates });
  }, []);

  const selectLayer = useCallback((layerId: string | null) => {
    dispatch({ type: 'SELECT_LAYER', layerId });
  }, []);

  const connectLayers = useCallback((fromId: string, toId: string) => {
    dispatch({ type: 'CONNECT_LAYERS', fromId, toId });
  }, []);

  const disconnectLayers = useCallback((fromId: string, toId: string) => {
    dispatch({ type: 'DISCONNECT_LAYERS', fromId, toId });
  }, []);

  const updateClassificationLabels = useCallback((labels: ClassificationLabel[]) => {
    dispatch({ type: 'UPDATE_CLASSIFICATION_LABELS', labels });
  }, []);

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const duplicateLayer = useCallback((layerId: string) => {
    dispatch({ type: 'DUPLICATE_LAYER', layerId });
  }, []);

  // Export in backend-compatible format (architecture.json)
  const getArchitectureJSON = useCallback(() => {
    if (!state.architecture) {
      return JSON.stringify({ layers: [] }, null, 2);
    }
    const backendArch = convertFrontendToBackend(state.architecture);
    return JSON.stringify(backendArch, null, 2);
  }, [state.architecture]);

  const value: ModelBuilderContextType = {
    architecture: state.architecture,
    selectedLayerId: state.selectedLayerId,
    layers: state.layers,
    history: state.history,
    historyIndex: state.historyIndex,
    createNewArchitecture,
    loadArchitecture,
    addLayer,
    removeLayer,
    updateLayer,
    selectLayer,
    connectLayers,
    disconnectLayers,
    updateClassificationLabels,
    undo,
    redo,
    reset,
    getArchitectureJSON,
    duplicateLayer,
  };

  return (
    <ModelBuilderContext.Provider value={value}>
      {children}
    </ModelBuilderContext.Provider>
  );
};

export const useModelBuilder = () => {
  const context = useContext(ModelBuilderContext);
  if (context === undefined) {
    throw new Error('useModelBuilder must be used within ModelBuilderProvider');
  }
  return context;
};
